import { ReportHeader } from '../../header/report-header';
import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatDate } from '../../../../utils/date-format';
import { InstallmentBankTransferSideBarFilter } from './side-bar/installment-bank-transfer-side-bar-filter';
import { Tag } from 'primereact/tag';
import { getReportInstallmentBankTransferOperation } from './operations/get-report-installment-bank-transfer-operation';
import { InstallmentBankTransferReportRow } from '../../../../api/types/reports/accountant/installment-bank-transfer-report.type';
import { PaymentInstallmentStatus } from '../../../../api/types/reports/accountant/installment.type';
import { InstallmentBankTransferSideBarFilterType } from './side-bar/installment-bank-transfer-side-bar-filter.type';

export const ReportInstallmentBankTransfer = () => {
  const [data, setData] = useState<InstallmentBankTransferReportRow[]>();
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] =
    useState<InstallmentBankTransferSideBarFilterType>({
      selectedMonth: new Date(),
      paymentStatuses: [],
    });

  const fetchReportData = async (
    filters: InstallmentBankTransferSideBarFilterType,
  ) => {
    await getReportInstallmentBankTransferOperation(
      filters,
      setData,
      setLoading,
    );
  };

  const getStatusSeverity = (
    status: PaymentInstallmentStatus,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case PaymentInstallmentStatus.PAID:
        return 'success';
      case PaymentInstallmentStatus.PENDING:
        return 'warning';
      case PaymentInstallmentStatus.FAILED:
      case PaymentInstallmentStatus.CANCELLED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  const getStatusLabel = (status: PaymentInstallmentStatus): string => {
    switch (status) {
      case PaymentInstallmentStatus.PENDING:
        return 'ממתין לתשלום';
      case PaymentInstallmentStatus.PAID:
        return 'שולם';
      case PaymentInstallmentStatus.FAILED:
        return 'נכשל';
      case PaymentInstallmentStatus.CANCELLED:
        return 'בוטל';
      default:
        return 'לא ידוע';
    }
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח הוראות קבע בנקאיות"
        description="דו״ח זה מציג מידע אודות כלל הוראות הקבע הבנקאיות של האירגון. הדו״ח מאפשר מעקב אחר תשלומים חודשיים לפי סטטוס ותאריך."
        metadata={[
          {
            label: 'מטרת הדו״ח',
            value: 'מעקב אחר הוראות קבע בנקאיות וסטטוס התשלומים החודשיים',
          },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="accounting"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <InstallmentBankTransferSideBarFilter onSearch={fetchReportData} />
        </div>

        {/* Table Section */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <DataTable
            value={data}
            rowHover
            loading={loading}
            rowClassName={() => 'cursor-pointer'}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="installmentPlanId"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{
              direction: 'rtl',
            }}
            className="installment-bank-transfer-table"
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="firstName"
              header="שם משלם"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <label>
                  {rowData.firstName} {rowData.lastName}
                </label>
              )}
            />
            <Column
              field="bankNumber"
              header="בנק"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="branchNumber"
              header="סניף"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="accountNumber"
              header="חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="totalAmount"
              header="סכום כולל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => `₪${rowData.totalAmount?.toLocaleString()}`}
            />
            <Column
              field="numberOfInstallments"
              header="מספר תשלומים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="installmentNumber"
              header="תשלום מספר"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                `${rowData.installmentNumber} / ${rowData.numberOfInstallments}`
              }
            />
            <Column
              field="amount"
              header="סכום תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => `₪${rowData.amount?.toLocaleString()}`}
            />
            <Column
              field="dueDate"
              header="תאריך לתשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.dueDate)}
            />
            <Column
              field="paidAt"
              header="תאריך תשלום בפועל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) =>
                rowData.paidAt ? formatDate(rowData.paidAt) : '-'
              }
            />
            <Column
              field="firstPaymentDate"
              header="תאריך תשלום ראשון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => formatDate(rowData.firstPaymentDate)}
            />
            <Column
              field="paymentDayOfMonth"
              header="יום חיוב בחודש"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="status"
              header="סטטוס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <Tag
                  style={{ minWidth: '100px' }}
                  value={getStatusLabel(rowData.status)}
                  severity={getStatusSeverity(rowData.status)}
                />
              )}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};
